/**
 * @file order-legal-terms.ts
 * @description Что показывает блок «Принятые условия» карточки заказа
 * (phase19.16, партия «Юридические документы», шаг Б; SB143;
 * uiux-legal-steps-b-c §2.3): строки принятых редакций по порядку
 * «оферта → политика → возврат», сноска о переносе по дате и тексты
 * состояний (заказ не с сайта, заказ до ввода редакций, документ не был
 * опубликован). Чистая функция — проверяется тестом.
 */

import { LEGAL_ACCEPTANCE_ROLES, type LegalAcceptanceDto, type LegalAcceptanceRole } from "@chaiberi/shared";

import { ORDER_SOURCE_LABEL, type OrderDto } from "./api-orders";
import { formatShopDate } from "./shop-time";


/** Нет строки документа в заказе после ввода редакций (SB125): тон и текст. */
const MISSING_TEXT: Readonly<Record<LegalAcceptanceRole, { tone: "attention" | "neutral"; text: string }>> = {
  OFFER: { tone: "attention", text: "Оферта не была опубликована в момент заказа — покупатель не видел её текста." },
  PRIVACY_POLICY: {
    tone: "attention",
    text: "Политика обработки персональных данных не была опубликована в момент заказа — покупатель не видел её текста.",
  },
  RETURNS: { tone: "neutral", text: "Условия возврата не были опубликованы в момент заказа." },
};

export type OrderLegalTermsView =
  /** Сервер поле не отдаёт — блока нет. */
  | { kind: "hidden" }
  /** Заказ не с сайта: условия сайта покупатель не принимал. */
  | { kind: "notSite"; text: string }
  /** Заказ до ввода редакций, строк нет. */
  | { kind: "noRevisions"; text: string }
  | {
      kind: "rows";
      rows: LegalAcceptanceDto[];
      missing: Array<{ role: LegalAcceptanceRole; tone: "attention" | "neutral"; text: string }>;
      /** Сноска «по дате заказа» — одна на блок, если есть перенесённая строка. */
      footnote: string | null;
    };

/** Вид блока по заказу. */
/**
 * `legalAcceptanceSince` (сервер) — с какого момента магазин записывает
 * редакции при заказе (первая непереносная запись; null — ещё не было).
 * Заказ раньше этого момента (или момента ещё нет) — пропуски документов не
 * считаются нарушением: строк могло не быть по переносу.
 */
export function orderLegalTermsView(order: Pick<OrderDto, "source" | "createdAt" | "legalAcceptances" | "legalAcceptanceSince">): OrderLegalTermsView {
  if (order.legalAcceptances === undefined) return { kind: "hidden" };
  if (order.source !== "SITE") {
    return {
      kind: "notSite",
      text: `Заказ создан не на сайте (источник: ${ORDER_SOURCE_LABEL[order.source]}) — условия сайта покупатель при оформлении не принимал.`,
    };
  }
  const byRole = new Map(order.legalAcceptances.map((row) => [row.role, row] as const));
  const since = order.legalAcceptanceSince ?? null;
  const before = since === null || Date.parse(order.createdAt) < Date.parse(since);
  if (byRole.size === 0 && before) {
    return {
      kind: "noRevisions",
      text: "Редакции не записаны: заказ оформлен до начала записи редакций, и действовавшую редакцию по дате определить не удалось.",
    };
  }
  const rows = LEGAL_ACCEPTANCE_ROLES.flatMap((role) => byRole.get(role) ?? []);
  // Пропуски — только для заказов после ввода редакций: до него строк могло не быть по переносу.
  const missing = before ? [] : LEGAL_ACCEPTANCE_ROLES.filter((role) => !byRole.has(role)).map((role) => ({ role, ...MISSING_TEXT[role] }));
  const footnote = rows.some((row) => row.backfilled)
    ? "Заказ оформлен до начала записи редакций: редакцию определили по дате заказа — она действовала в момент оформления."
    : null;
  return { kind: "rows", rows, missing, footnote };
}

/** «ред. 4 от 12.10.2026». */
export function revisionLine(row: Pick<LegalAcceptanceDto, "number" | "publishedAt">): string {
  return `ред. ${row.number} от ${formatShopDate(row.publishedAt)}`;
}
